import type { Localized } from '../types/funnel';

export interface HelpAnswer {
  q: Localized;
  a: Localized;
}

/** Short contextual answers per screen. Keys match screen ids used in the funnel. Plain punctuation, no em dashes. */
export const helpAnswers: Record<string, HelpAnswer[]> = {
  home: [
    {
      q: { en: 'Is this a fixed price?', es: '¿Es un precio fijo?' },
      a: {
        en: 'No. We bill by the hour for actual service time. You’ll see an itemized estimate before you commit to anything, and your final bill also reflects any changes you approve.',
        es: 'No. Cobramos por hora según el tiempo real de servicio. Verá un estimado detallado antes de comprometerse, y su factura final también refleja los cambios que usted apruebe.',
      },
    },
    {
      q: { en: 'Do I have to call?', es: '¿Tengo que llamar?' },
      a: {
        en: 'No. You can plan everything here. If you’d like help, our phone line answers 24/7 at (656) 268-0800 and can stay with you while you finish online. For a person on our team, call (813) 851-0590.',
        es: 'No. Puede planearlo todo aquí. Si quiere ayuda, nuestra línea telefónica contesta 24/7 al (656) 268-0800 y puede acompañarle mientras termina en línea. Para hablar con una persona del equipo, llame al (813) 851-0590.',
      },
    },
  ],
  stepSize: [
    {
      q: { en: 'Do I need to know everything now?', es: '¿Necesito saberlo todo ahora?' },
      a: { en: 'No. Pick what’s closest. You can change any answer later and the estimate updates.', es: 'No. Elija lo más cercano. Puede cambiar cualquier respuesta y el estimado se actualiza.' },
    },
  ],
  stepAddresses: [
    {
      q: { en: 'Which areas do you serve?', es: '¿Qué áreas atienden?' },
      a: {
        en: 'Our base is in Tampa. We focus on Tampa, Orlando, Ocala and Sarasota, and run approved routes to Fort Myers, Naples, Gainesville, Jacksonville, Daytona and Miami. Longer routes include a transportation estimate. If your route is outside our area, we keep your details and our team reviews options with you.',
        es: 'Nuestra base está en Tampa. Atendemos principalmente Tampa, Orlando, Ocala y Sarasota, y tenemos rutas aprobadas a Fort Myers, Naples, Gainesville, Jacksonville, Daytona y Miami. Las rutas largas incluyen un estimado de transporte. Si su ruta está fuera del área, guardamos sus datos y nuestro equipo revisa opciones con usted.',
      },
    },
  ],
  stepAccess: [
    {
      q: { en: 'Do you charge for stairs?', es: '¿Cobran por escaleras?' },
      a: {
        en: 'There’s no separate stair fee in this estimate. Stairs, elevators and long walks take more time, which is billed hourly, so telling us now keeps the estimate accurate.',
        es: 'No hay un cargo separado por escaleras en este estimado. Escaleras, elevadores y caminatas largas toman más tiempo, que se cobra por hora, así que indicarlo ahora mantiene el estimado preciso.',
      },
    },
  ],
  stepInventory: [
    {
      q: { en: 'Not sure about every item?', es: '¿No está seguro de cada artículo?' },
      a: { en: 'Add what you know and tick “I’m not sure yet.” Our team will confirm the list with you before move day.', es: 'Agregue lo que sepa y marque “Aún no estoy seguro”. Nuestro equipo confirmará la lista con usted antes de la mudanza.' },
    },
  ],
  stepPacking: [
    {
      q: { en: 'Does packing myself lower the cost?', es: '¿Empacar yo mismo baja el costo?' },
      a: {
        en: 'It avoids the separately priced packing service and materials, and having boxes ready usually shortens service time. Actual time on the day decides the hourly part.',
        es: 'Evita el servicio de empaque y los materiales que se cotizan aparte, y tener las cajas listas normalmente acorta el tiempo de servicio. El tiempo real del día define la parte por hora.',
      },
    },
    {
      q: { en: 'Is furniture protection included?', es: '¿La protección de muebles está incluida?' },
      a: {
        en: 'Yes. Your crew pads and wraps furniture as part of the move. Packing boxes for you and selling cartons or supplies are separate, optional services.',
        es: 'Sí. El equipo acolcha y envuelve los muebles como parte de la mudanza. Empacar cajas por usted y vender cajas o materiales son servicios aparte y opcionales.',
      },
    },
  ],
  stepDate: [
    {
      q: { en: 'I’m not sure of the date', es: 'No estoy seguro de la fecha' },
      a: { en: 'That’s fine. We’ll save your plan without holding a truck or asking for a date-specific deposit.', es: 'Está bien. Guardamos su plan sin apartar un camión ni pedir un depósito para una fecha específica.' },
    },
  ],
  stepContact: [
    {
      q: { en: 'I need to check with my spouse', es: 'Necesito consultarlo con mi pareja' },
      a: {
        en: 'Your answers are saved on this device. Once you send your plan, our team can share it so you can decide together.',
        es: 'Sus respuestas se guardan en este dispositivo. Al enviar su plan, nuestro equipo puede compartirlo para que decidan juntos.',
      },
    },
  ],
  stepEstimate: [
    {
      q: { en: 'Why the travel hour?', es: '¿Por qué la hora de traslado?' },
      a: {
        en: 'It covers the crew and truck driving from our Tampa-area base to you and back on a local move. It’s one billed hour, separate from service time. Longer Florida routes get a transportation estimate instead.',
        es: 'Cubre el viaje del equipo y el camión desde nuestra base hasta usted y de regreso en una mudanza local. Es una hora facturada aparte del tiempo de servicio. Las rutas largas en Florida llevan un estimado de transporte.',
      },
    },
    {
      q: { en: 'Why the operating surcharge?', es: '¿Por qué el recargo operativo?' },
      a: {
        en: 'The 20% operating surcharge covers costs like truck use, wear and applicable tolls. We show its amount once its calculation is confirmed on your estimate.',
        es: 'El recargo operativo del 20% cubre costos como uso y desgaste del camión y peajes aplicables. Mostramos el monto cuando su cálculo se confirme en su estimado.',
      },
    },
    {
      q: { en: 'Can I choose two movers?', es: '¿Puedo elegir dos trabajadores?' },
      a: {
        en: 'Yes. If we recommend three, it’s for safe handling and a shorter day. Our team confirms two can do the job safely before your move.',
        es: 'Sí. Si recomendamos tres, es por manejo seguro y un día más corto. Nuestro equipo confirma que dos pueden hacerlo con seguridad antes de la mudanza.',
      },
    },
    {
      q: { en: 'What could change the final bill?', es: '¿Qué puede cambiar la factura final?' },
      a: {
        en: 'Actual service time, plus any change you approve, such as added items, stops or services. The minimum, travel hour, surcharge and card charge are listed here so nothing is a surprise.',
        es: 'El tiempo real de servicio, más cualquier cambio que usted apruebe, como artículos, paradas o servicios agregados. El mínimo, la hora de traslado, el recargo y el cargo de tarjeta aparecen aquí para que nada le sorprenda.',
      },
    },
  ],
  stepReserve: [
    {
      q: { en: 'Do I have to pay everything now?', es: '¿Tengo que pagar todo ahora?' },
      a: { en: 'No. A $99 deposit reserves your move and is credited to your final bill. The rest is settled after the move based on actual time.', es: 'No. Un depósito de $99 reserva su mudanza y se acredita a su factura final. El resto se liquida después según el tiempo real.' },
    },
    {
      q: { en: 'I’m not the payer', es: 'No soy quien paga' },
      a: { en: 'Choose “Someone else is paying.” The cardholder signs their own authorization. You never sign for them.', es: 'Elija “Otra persona paga”. El titular de la tarjeta firma su propia autorización. Usted nunca firma por esa persona.' },
    },
  ],
};
